// Basic
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
// Common
import SIM_OFFERS_PROVIDERS from '@/app/modules/sims/enums/SimOffersProviders';
// Lang
import { nsOrganization, nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.setFormData
 * @param root0.simProviders
 * @param root0.defaultValues
 * @returns {JSX} FormDetails
 */
function FormDetails({ simProviders, setFormData, defaultValues }) {

    // Form
    const formSchema = z.object({
        simProvider: z.string().optional(),
        quantity: z.number({ ['invalid_type_error']: '' }).nonnegative('')
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            simProvider: '',
            quantity: '',
            ...defaultValues
        }
    });

    // Functions
    const getSimProviders = () => {
        return simProviders.map((item) => {
            const provider = SIM_OFFERS_PROVIDERS[item.providerName];
            return {
                value: provider.key,
                name: provider.entities.name,
                namespace: nsOrganization
            };
        }) || [];
    };

    const getProviderQuantities = () => {
        const selectedProvider = Object.values(SIM_OFFERS_PROVIDERS).find((item) => item.key === form.getValues('simProvider'));

        if (selectedProvider) {
            const provider = simProviders.find((item) => item.providerName === selectedProvider.key);

            return provider.quantities.map((quantity) => {
                return {
                    value: quantity,
                    name: quantity
                };
            });
        }
        return [];
    };

    // Contansts
    // Memoized values
    const simProvidersOptions = useMemo(() => getSimProviders(), [simProviders]);
    const providerQuantitiesOptions = useMemo(() => getProviderQuantities(), [simProviders, form.getValues('simProvider')]);

    // Form Watchers
    const simProvider = useWatch({ control: form.control, name: 'simProvider' });
    const quantity = useWatch({ control: form.control, name: 'quantity' });
    const isValid = form.formState.isValid;

    // Hooks
    // If only one provider is available
    useEffect(() => {
        if (simProviders && simProviders.length === 1) {
            form.setValue('simProvider', SIM_OFFERS_PROVIDERS[simProviders[0].providerName].key);
        }
    }, [simProviders]);

    // Update details form Data
    useEffect(() => {
        form.trigger();
        setFormData({
            simProvider,
            quantity,
            isValid
        });
    }, [isValid, simProvider, quantity]);

    return (
        <Form {...form}>
            {simProviders.length > 1 &&
                <div className='grid grid-cols-[30%_70%] items-center'>
                    <FormLabel required htmlFor='iccid' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.order_sim.form.provider' namespace={nsSimCard} />
                    </FormLabel>
                    <FormField
                        name='simProvider'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input sm:w-[60%]'>
                                <FormControl>
                                    <Select
                                        {...field}
                                        options={simProvidersOptions}
                                        placeholder='modal.order_sim.form.provider_placeholder'
                                        namespace={nsSimCard}
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value);
                                            // Set quantity field to empty when simPovider field is changed
                                            form.setValue('quantity', '');
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
            }
            <div className='grid grid-cols-[30%_70%] items-center'>
                <FormLabel required htmlFor='iccid' className='block text-sm font-medium text-gray-700'>
                    <Entity entity='modal.order_sim.form.quantity' namespace={nsSimCard} />
                </FormLabel>
                <FormField
                    name='quantity'
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className='addresses-list-input sm:w-[20%]'>
                            <FormControl>
                                <Select
                                    {...field}
                                    placeholder='--'
                                    options={providerQuantitiesOptions}
                                    namespace={nsSimCard}
                                    value={field.value}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
        </Form>
    );
}

FormDetails.propTypes = {
    simProviders: PropTypes.array,
    setFormData: PropTypes.func,
    defaultValues: PropTypes.object
};
export default FormDetails;